import { RenewalStatus } from '@appTypes/enums';
import { Program } from '@shared/constants';

import type { RosterScout } from '../duck/types';
import {
  peopleFilters,
  programGrownUpAges,
  registrationSituationStatuses,
} from './constants';
import type { RosterSlice } from './types';
import { getIsDenChief } from './utilsTyped';

/** Don't show parents in the roster */
export function filterOutParents(scout: RosterScout) {
  if (scout.isAdult && !scout.isLeader) return false;
  return true;
}

export function filterByRenewalStatus(
  scout: RosterScout,
  filters: RosterSlice['filter'],
) {
  // Normilize value for the case of "Eligible to Renew (unit only)"
  const [value] = scout.renewalStatus.split(' (unit only)');
  // if scout not in akela roster it's set as needs to register, in such case we want to count it as expired
  // either as renewal or registration. See SBL-6303
  if (
    (value === RenewalStatus.NeedsToRegister ||
      value === RenewalStatus.Expired) &&
    filters[registrationSituationStatuses.EXPIRED]
  ) {
    return true;
  }

  const filterCheck = !!filters[value as keyof typeof filters];

  return filterCheck;
}

export function filterByRegistrationStatus(
  scout: RosterScout,
  filters: RosterSlice['filter'],
) {
  const value = scout.registrationSituationStatus;

  // if scout not in akela roster it's set as needs to register, in such case we want to count it as expired
  // either as renewal or registration. See SBL-6303
  if (
    scout.renewalStatus === RenewalStatus.NeedsToRegister &&
    filters[registrationSituationStatuses.EXPIRED]
  ) {
    return true;
  }

  const filterCheck = !!filters[value];
  return filterCheck;
}

/**
 * Decides whether a scout passes the "type" filters (Youth, Aged Youth, Adults,
 * Den Chiefs). A scout is included when they match any enabled category.
 *
 * In Cub Scout packs den chiefs are their own filter category. They are also
 * youth-aged, so unless we exclude them from the Youth / Aged Youth checks they
 * would always be included through those filters and the Den Chief filter would
 * never take effect. See SBL-6419.
 */
export function isMemberOf(
  scout: RosterScout,
  filters: RosterSlice['filter'],
  program: Program,
) {
  const oldAge = programGrownUpAges[program] ?? 18;
  const age = Number(scout.age);
  const isAdult = scout.isAdult;
  const isUnitParticipant = scout.isUnitParticipant ?? false;
  const isCubDenChief = program === Program.CUB_SCOUT && getIsDenChief(scout);

  // filter or include adults
  if (filters[peopleFilters.ADULTS] && isAdult && !isUnitParticipant) {
    return true;
  }

  // filter or include aged youth
  if (
    filters[peopleFilters.AGED_YOUTH] &&
    !isAdult &&
    age >= oldAge &&
    !isCubDenChief
  ) {
    return true;
  }

  // filter or include youth
  if (filters[peopleFilters.YOUTH] && age < oldAge && !isCubDenChief) {
    return true;
  }

  // filter or include den chiefs
  if (isCubDenChief && filters[peopleFilters.DEN_CHIEFS]) {
    return true;
  }

  // fallback if no filters match
  return false;
}
